/**
 * Physics System - Cannon.js physics world management
 */

import * as CANNON from 'cannon-es';
import * as THREE from 'three';
import { GAME_BOUNDARY_SIZE } from '../utils/constants.js';

export class PhysicsSystem {
    constructor(config = {}) {
        this.world = null;
        this.config = {
            gravity: config.gravity ?? 0,
            timestep: config.timestep ?? 1 / 60,
            maxSubSteps: Math.max(1, config.maxSubSteps ?? 1), // 从2降到1，减少物理计算量
            iterations: config.iterations ?? 5 // 从8降到5，减少求解器迭代
        };

        // Debug
        this.debugRenderer = null;
        this.isDebugEnabled = false;

        // Physics materials
        this.materials = {};

        // Contact materials (collision behaviors)
        this.contactMaterials = {};

        // 延迟处理队列，避免在物理步进过程中修改世界状态
        this.pendingAdds = new Set();
        this.pendingRemoves = new Set();
        this.isStepping = false;

        this.lastStats = {
            contacts: 0,
            bodies: 0,
            sleeping: 0,
            active: 0
        };
    }

    init() {

        // Create physics world
        this.world = new CANNON.World({
            gravity: new CANNON.Vec3(0, this.config.gravity, 0)
        });

        // Configure solver (优化以减少抖动和运算成本)
        this.world.solver.iterations = Math.floor(this.config.iterations); // 使用配置的迭代次数
        this.world.solver.tolerance = 0.006; // 提高容差以加快求解速度

        // 添加额外的稳定性设置
        this.world.defaultContactMaterial.contactEquationStiffness = 2e5;
        this.world.defaultContactMaterial.contactEquationRelaxation = 7;
        this.world.defaultContactMaterial.frictionEquationStiffness = 2e5;
        this.world.defaultContactMaterial.frictionEquationRelaxation = 5;

        // Set up broadphase (collision detection optimization) - 使用更优化的算法
        this.world.broadphase = new CANNON.SAPBroadphase(this.world);
        this.world.broadphase.useBoundingBoxes = true;

        // 设置允许睡眠以提高性能和稳定性
        this.world.allowSleep = true;
        this.world.sleepSpeedLimit = 0.15; // 提高速度阈值，让更多物体休眠
        this.world.sleepTimeLimit = 0.8; // 缩短休眠等待时间，更快进入休眠

        // Create physics materials
        this.createMaterials();

        // Create world boundaries
        this.createWorldBoundaries();

    }

    createMaterials() {
        // Ship material (moderate friction, moderate bounce)
        this.materials.ship = new CANNON.Material('ship');
        this.materials.ship.friction = 0.4;
        this.materials.ship.restitution = 0.3;

        // Projectile material (low friction, moderate bounce)
        this.materials.projectile = new CANNON.Material('projectile');
        this.materials.projectile.friction = 0.1;
        this.materials.projectile.restitution = 0.6;

        // Ocean material (high friction for water resistance)
        this.materials.ocean = new CANNON.Material('ocean');
        this.materials.ocean.friction = 0.8;
        this.materials.ocean.restitution = 0.1;

        // Ground/boundary material (降低摩擦力，避免移动困难)
        this.materials.boundary = new CANNON.Material('boundary');
        this.materials.boundary.friction = 0.05; // 从0.3降低到0.05，减少阻力
        this.materials.boundary.restitution = 0.1; // 稍微降低弹性

        // Create contact materials (how materials interact)
        this.createContactMaterials();
    }

    createContactMaterials() {
        // Ship to ship collision
        this.contactMaterials.shipToShip = new CANNON.ContactMaterial(
            this.materials.ship,
            this.materials.ship,
            {
                friction: 0.4,
                restitution: 0.5
            }
        );
        this.world.addContactMaterial(this.contactMaterials.shipToShip);

        // Projectile to ship collision
        this.contactMaterials.projectileToShip = new CANNON.ContactMaterial(
            this.materials.projectile,
            this.materials.ship,
            {
                friction: 0.1,
                restitution: 0.2
            }
        );
        this.world.addContactMaterial(this.contactMaterials.projectileToShip);

        // Ship to boundary collision (减少摩擦阻力和抖动)
        this.contactMaterials.shipToBoundary = new CANNON.ContactMaterial(
            this.materials.ship,
            this.materials.boundary,
            {
                friction: 0.02, // 极低摩擦力避免抖动
                restitution: 0.1, // 极低弹性避免弹跳
                contactEquationStiffness: 1e6, // 增加接触刚度
                contactEquationRelaxation: 4, // 增加松弛时间
                frictionEquationStiffness: 1e6,
                frictionEquationRelaxation: 3
            }
        );
        this.world.addContactMaterial(this.contactMaterials.shipToBoundary);
    }

    createWorldBoundaries() {
        const boundarySize = GAME_BOUNDARY_SIZE; // 与视觉边界匹配
        const boundaryHeight = 10;
        const boundaryThickness = 2; // 较薄的边界，减少碰撞问题

        // 创建物理边界墙，与视觉边界精确匹配
        const boundaries = [
            // 北墙 (上边界)
            { pos: [0, boundaryHeight/2, -boundarySize], size: [boundarySize * 2, boundaryHeight, boundaryThickness] },
            // 南墙 (下边界)
            { pos: [0, boundaryHeight/2, boundarySize], size: [boundarySize * 2, boundaryHeight, boundaryThickness] },
            // 东墙 (右边界)
            { pos: [boundarySize, boundaryHeight/2, 0], size: [boundaryThickness, boundaryHeight, boundarySize * 2] },
            // 西墙 (左边界)
            { pos: [-boundarySize, boundaryHeight/2, 0], size: [boundaryThickness, boundaryHeight, boundarySize * 2] }
        ];

        this.boundaryBodies = []; // 存储边界物理体引用

        boundaries.forEach((boundary, index) => {
            const shape = new CANNON.Box(new CANNON.Vec3(...boundary.size.map(s => s / 2)));
            const body = new CANNON.Body({
                mass: 0, // 静态物体
                material: this.materials.boundary,
                type: CANNON.Body.KINEMATIC // 设为运动学体，更稳定的碰撞
            });
            body.addShape(shape);
            body.position.set(...boundary.pos);

            // 添加碰撞组，确保只与船只碰撞
            body.collisionFilterGroup = 1;
            body.collisionFilterMask = 2; // 只与船只碰撞

            this.world.addBody(body);
            this.boundaryBodies.push(body);
        });

    }

    update(deltaTime) {
        if (!this.world) return;

        this.flushPendingOperations();

        this.sanitizeWorldBodies();

        // Step the physics simulation
        this.isStepping = true;
        try {
            const maxSubSteps = deltaTime > this.config.timestep * 1.25 ? this.config.maxSubSteps : 1;
            this.world.step(this.config.timestep, deltaTime, maxSubSteps);
        } finally {
            this.isStepping = false;
        }

        this.flushPendingOperations();

        // Update debug renderer if enabled
        if (this.isDebugEnabled && this.debugRenderer) {
            this.debugRenderer.update();
        }

        this.captureStepStats();
    }

    captureStepStats() {
        if (!this.world) {
            this.lastStats = { contacts: 0, bodies: 0, sleeping: 0, active: 0 };
            return;
        }

        const bodies = this.world.bodies || [];
        let sleeping = 0;
        for (let i = 0; i < bodies.length; i++) {
            if (bodies[i].sleepState === CANNON.Body.SLEEPING) {
                sleeping += 1;
            }
        }

        const contacts = this.world.narrowphase?.contactEquations?.length ?? 0;

        this.lastStats = {
            contacts,
            bodies: bodies.length,
            sleeping,
            active: bodies.length - sleeping
        };
    }

    getStepStats() {
        return this.lastStats;
    }

    // Add a rigid body to the physics world
    addBody(body) {
        if (!this.world || !(body instanceof CANNON.Body)) {
            if (body !== undefined) {
                console.warn('尝试添加无效的物理体，已忽略:', body);
            }
            return;
        }

        if (this.isStepping) {
            this.pendingRemoves.delete(body);
            this.pendingAdds.add(body);
            return;
        }

        if (!this.world.bodies.includes(body)) {
            this.world.addBody(body);
        }
    }

    // Remove a rigid body from the physics world
    removeBody(body) {
        if (!this.world || !(body instanceof CANNON.Body)) {
            return;
        }

        if (this.isStepping) {
            this.pendingAdds.delete(body);
            this.pendingRemoves.add(body);
            return;
        }

        if (this.world.bodies.includes(body)) {
            this.world.removeBody(body);
        }
    }

    // Create a ship physics body
    createShipBody(position, size = { width: 3, height: 1, length: 6 }) {
        // Use compound spheres along the hull for robust Trimesh collisions
        const body = new CANNON.Body({
            mass: 100,
            material: this.materials.ship
        });

        const halfLength = size.length / 2; // ~3
        const spacing = size.length / 3;     // ~2
        const baseRadius = Math.max(size.width, size.height) * 0.6; // ~1.8

        // Three spheres: stern, mid, bow (slightly tapered toward bow)
        const rStern = baseRadius * 0.9; // ~1.62
        const rMid   = baseRadius * 1.0; // ~1.8
        const rBow   = baseRadius * 0.8; // ~1.44

        body.addShape(new CANNON.Sphere(rStern), new CANNON.Vec3(0, 0, -spacing));
        body.addShape(new CANNON.Sphere(rMid),   new CANNON.Vec3(0, 0, 0));
        body.addShape(new CANNON.Sphere(rBow),   new CANNON.Vec3(0, 0, spacing));

        body.position.set(position.x, position.y, position.z);

        // 设置碰撞组，确保与边界碰撞
        body.collisionFilterGroup = 2; // 船只碰撞组
        body.collisionFilterMask = 1 | 2 | 4 | 8; // 与边界、其他船只、炮弹、建筑物碰撞

        // Add linear and angular damping for realistic movement
        body.linearDamping = 0.4;  // Water resistance
        body.angularDamping = 0.8; // Rotational resistance

        body.sleepSpeedLimit = 0.25;
        body.sleepTimeLimit = 0.9;

        // 添加边界检查回调 - 碰撞时增加阻尼以减少抖动
        body.addEventListener('collide', (event) => {
            const { contact, target } = event;
            // 如果碰撞的是边界，增加临时阻尼减少抖动
            if (target.collisionFilterGroup === 1) {
                // 临时增加线性和角度阻尼
                body.linearDamping = Math.min(body.linearDamping + 0.2, 0.9);
                body.angularDamping = Math.min(body.angularDamping + 0.3, 0.95);

                // 0.5秒后恢复正常阻尼
                setTimeout(() => {
                    body.linearDamping = Math.max(body.linearDamping - 0.2, 0.4);
                    body.angularDamping = Math.max(body.angularDamping - 0.3, 0.8);
                }, 500);
            }
        });

        return body;
    }

    // Create a projectile physics body
    createProjectileBody(position, size = 0.2) {
        // Create sphere shape for projectile
        const shape = new CANNON.Sphere(size);

        // Create rigid body
        const body = new CANNON.Body({
            mass: 1,
            material: this.materials.projectile
        });

        body.addShape(shape);
        body.position.set(position.x, position.y, position.z);

        // 设置炮弹碰撞组
        body.collisionFilterGroup = 4; // 炮弹碰撞组
        body.collisionFilterMask = 1 | 2 | 8; // 与边界、船只、建筑物碰撞 (不与其他炮弹碰撞)

        // Less damping for projectiles
        body.linearDamping = 0.1;
        body.angularDamping = 0.1;

        return body;
    }

    // Apply force to simulate ship movement
    applyShipMovementForce(body, direction, force = 500) {
        if (!body) return;

        // Apply force in world space
        const forceVector = new CANNON.Vec3(
            direction.x * force,
            0, // No vertical force
            direction.z * force
        );

        body.applyForce(forceVector, body.position);
    }

    // Apply torque for ship rotation
    applyShipRotationTorque(body, torque) {
        if (!body) return;

        const torqueVector = new CANNON.Vec3(0, torque, 0);
        body.applyTorque(torqueVector);
    }

    // Raycast for mouse picking and targeting
    raycast(from, to, options = {}) {
        const result = new CANNON.RaycastResult();

        this.world.raycastClosest(
            new CANNON.Vec3(from.x, from.y, from.z),
            new CANNON.Vec3(to.x, to.y, to.z),
            options,
            result
        );

        return result;
    }

    // Enable debug rendering
    enableDebug(scene) {
        if (!this.world || !scene) return;

        try {
            // Note: cannon-es-debugger is an optional dependency for debug rendering
            // For now, we'll use a simple wireframe approach
            this.isDebugEnabled = true;
        } catch (error) {
            console.warn('Debug renderer not available:', error);
        }
    }

    // Disable debug rendering
    disableDebug() {
        this.isDebugEnabled = false;
        if (this.debugRenderer) {
            this.debugRenderer = null;
        }
    }

    // Sync Three.js object with Cannon.js body
    static syncMeshWithBody(mesh, body) {
        if (!mesh || !body) return;

        // Copy position
        mesh.position.copy(body.position);

        // Copy rotation (convert quaternion)
        mesh.quaternion.copy(body.quaternion);
    }

    // Helper to create a collision group
    createCollisionGroup() {
        return Math.pow(2, this.world.bodies.length % 32);
    }

    // Clean up physics system
    dispose() {
        if (this.world) {
            // Remove all bodies
            while (this.world.bodies.length > 0) {
                this.world.removeBody(this.world.bodies[0]);
            }

            // Remove all contact materials
            this.world.contactMaterials = [];

            this.world = null;
        }

        this.debugRenderer = null;
    }

    flushPendingOperations() {
        if (!this.world || this.isStepping) return;

        if (this.pendingRemoves.size > 0) {
            this.pendingRemoves.forEach(body => {
                if (body instanceof CANNON.Body && this.world.bodies.includes(body)) {
                    this.world.removeBody(body);
                }
            });
            this.pendingRemoves.clear();
        }

        if (this.pendingAdds.size > 0) {
            this.pendingAdds.forEach(body => {
                if (body instanceof CANNON.Body && !this.world.bodies.includes(body)) {
                    this.world.addBody(body);
                }
            });
            this.pendingAdds.clear();
        }
    }

    sanitizeWorldBodies() {
        if (!this.world) return;

        const { bodies, collisionMatrix, idToBodyMap } = this.world;
        let removed = false;

        for (let i = bodies.length - 1; i >= 0; i--) {
            const body = bodies[i];
            const isValidBody = body instanceof CANNON.Body && typeof body.wakeUpAfterNarrowphase === 'boolean';

            if (!isValidBody) {
                const reason = body === undefined
                    ? 'undefined'
                    : body === null
                        ? 'null'
                        : !(body instanceof CANNON.Body)
                            ? `非CANNON.Body类型: ${body?.constructor?.name ?? '未知'}`
                            : '缺少wakeUpAfterNarrowphase属性';

                console.warn('检测到无效物理体，准备移除:', { index: i, reason, body });
                bodies.splice(i, 1);
                removed = true;
                continue;
            }

            if (typeof body.id === 'number' && idToBodyMap[body.id] !== body) {
                idToBodyMap[body.id] = body;
            }
        }

        if (removed) {
            collisionMatrix.setNumObjects(bodies.length);
            console.warn('检测到无效物理体引用，已清理。当前物理体数量:', bodies.length);
        }
    }
}
